import React, { useContext, useEffect, useMemo, useState } from 'react';
import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonCheckbox, IonChip, IonContent, IonFab, IonFabButton, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonPage, IonSelect, IonSelectOption, IonTitle, IonToolbar, IonImg } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { DataContext } from '../context/DataContext';
import Archiv from '../components/Archiv';
import Auswahl from '../components/Auswahl';
import { add, cash, warning } from 'ionicons/icons';
import Toolbar from '../components/Toolbar';
import { set } from 'react-hook-form';

type SettingsSection = 'all' | 'kundenkonto' | 'schuelerkonto';

interface SettingsProps {
  section?: SettingsSection;
  embedded?: boolean;
  onClose?: () => void;
  selectedSchuelerId?: any;
  onSelectedSchuelerIdChange?: (schuelerId: any) => void;
}

type SchoolOption = { value: string; label: string };

const normalizeString = (value: unknown) => String(value ?? '').trim();

const parseSchoolsAndClasses = (rawData: any): {
  schools: SchoolOption[];
  classMap: Record<string, string[]>;
} => {
  const normalizedData =
    rawData &&
    typeof rawData === 'object' &&
    !Array.isArray(rawData) &&
    Array.isArray((rawData as any).data)
      ? (rawData as any).data
      : rawData;

  const classSetBySchool = new Map<string, Set<string>>();
  const schoolLabelById = new Map<string, string>();

  const addClass = (schoolIdRaw: unknown, classNameRaw: unknown, schoolLabelRaw?: unknown) => {
    const schoolId = normalizeString(schoolIdRaw);
    const className = normalizeString(classNameRaw);
    const schoolLabel = normalizeString(schoolLabelRaw);

    if (!schoolId || !className) {
      return;
    }

    if (!classSetBySchool.has(schoolId)) {
      classSetBySchool.set(schoolId, new Set<string>());
    }
    classSetBySchool.get(schoolId)?.add(className);

    if (schoolLabel && !schoolLabelById.has(schoolId)) {
      schoolLabelById.set(schoolId, schoolLabel);
    }
  };

  if (Array.isArray(normalizedData)) {
    normalizedData.forEach((entry: any) => {
      if (!entry) {
        return;
      }

      const schoolId =
        entry?.campusmenu_klassen_betriebe_id ??
        entry?.betriebe_id ??
        entry?.schoolId ??
        entry?.school_id ??
        entry?.value ??
        entry?.id;

      const schoolLabel =
        entry?.campusmenu_klassen_betriebe_name ??
        entry?.betriebe_name ??
        entry?.schoolLabel ??
        entry?.schoolName ??
        entry?.school_name ??
        entry?.label ??
        entry?.name;

      const className =
        entry?.campusmenu_klassen_name ??
        entry?.klasse ??
        entry?.className;

      if (className !== undefined) {
        addClass(schoolId, className, schoolLabel);
      }
    });
  }

  const classMap: Record<string, string[]> = {};
  const schools: SchoolOption[] = [];

  classSetBySchool.forEach((classSet, schoolId) => {
    classMap[schoolId] = Array.from(classSet);
    schools.push({
      value: schoolId,
      label: schoolLabelById.get(schoolId) ?? `Schule ${schoolId}`,
    });
  });

  schools.sort((a, b) => a.label.localeCompare(b.label, 'de'));
  return { schools, classMap };
};

const Settings: React.FC<SettingsProps> = ({
  section = 'all',
  embedded = false,
  onClose,
  selectedSchuelerId: selectedSchuelerIdProp,
  onSelectedSchuelerIdChange,
}) => {
  const history = useHistory();
  const { 
    appLoggedIn, 
    appLoggedInChange,
    appUserData,
    appUserDataChange,
    appToken,
    appReloadSpeiseplanChange,
    appUserSpeiseplan,
    appUserVerzehr,
    appWarenkorb,
    appWarenkorbChange,
    appWarenkorbResetSignalChange,
    appSelectedSchuelerId,
    appSelectedSchuelerIdChange,

  } = useContext(DataContext);

  const schuelerListe = Array.isArray(appUserData?.schueler) ? appUserData.schueler : [];
  const kundennummer = `K${String(appUserData?.campusmenu_id || 0).padStart(6, '0')}`;
  const userVorname = appUserData?.vorname || appUserData?.campusmenu_user_vorname || '';
  const userNachname = appUserData?.nachname || appUserData?.campusmenu_user_nachname || '';
  const kontoInhaber = [userVorname, userNachname].filter(Boolean).join(' ').trim();

  const getSchuleLabel = (schueler: any) => {
    const rawSchule = String(
      schueler?.campusmenu_schueler_schule_name ??
      schueler?.schuelerdaten?.campusmenu_schueler_schule_name ??
      schueler?.campusmenu_schueler_schule ??
      schueler?.schuelerdaten?.campusmenu_schueler_schule ??
      schueler?.campusmenu_schueler_schule_id ??
      schueler?.schuelerdaten?.campusmenu_schueler_schule_id ??
      ''
    ).trim();

    if (!rawSchule) {
      return '';
    }

    if (rawSchule === '5') {
      return 'SEE-FAB - GS Seebek Fab';
    }

    if (rawSchule === '130') {
      return 'GySue - Gymnasium Süderelbe';
    }

    return rawSchule;
  };

  const getSchuelerLabel = (schueler: any) => {
    if (!schueler || typeof schueler !== 'object') {
      return '';
    }

    const campusmenu_schueler_id = String(schueler.campusmenu_schueler_id || '').padStart(6, '0');
    const nachnameWert = schueler.campusmenu_schueler_nachname || schueler?.schuelerdaten?.campusmenu_schueler_nachname || '';
    const vornameWert = schueler.campusmenu_schueler_vorname || schueler?.schuelerdaten?.campusmenu_schueler_vorname || '';
    const klasseWert = schueler.campusmenu_schueler_klasse || schueler?.schuelerdaten?.campusmenu_schueler_klasse || '';
    const schuleWert = getSchuleLabel(schueler);

    const name = [nachnameWert, vornameWert].filter(Boolean).join(', ');
    const klasseSchule = [klasseWert, schuleWert].filter(Boolean).join(' ');

    if (name && klasseSchule) {
      return `S${campusmenu_schueler_id} - ${name} (${klasseSchule})`;
    }

    return name || klasseSchule || 'Schueler';
  };

  const getSchuelerDatum = (schueler: any) => {
    const rawDatum =
      schueler?.campusmenu_schueler_geb_datum ||
      schueler?.campusmenu_schueler_datum ||
      schueler?.campusmenu_schueler_geburtstag ||
      schueler?.campusmenu_schueler_geburtsdatum ||
      schueler?.datum ||
      schueler?.geburtstag ||
      '';

    if (!rawDatum || typeof rawDatum !== 'string') {
      return '';
    }

    const isoMatch = rawDatum.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }

    const deMatch = rawDatum.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (deMatch) {
      return `${deMatch[3]}-${deMatch[2]}-${deMatch[1]}`;
    }

    return '';
  };

  const trimMax = (value: string, maxLen: number) => value.slice(0, maxLen);

  const sanitizeName = (value: string, maxLen: number) => {
    const cleaned = value.replace(/[^A-Za-zÀ-ÿ' -]/g, '');
    return trimMax(cleaned, maxLen);
  };

  const sanitizePhone = (value: string, maxLen: number) => {
    const cleaned = value.replace(/[^0-9+()\-/ ]/g, '');
    return trimMax(cleaned, maxLen);
  };

  const sanitizeStreet = (value: string, maxLen: number) => {
    const cleaned = value.replace(/[^0-9A-Za-zÀ-ÿ.,' -]/g, '');
    return trimMax(cleaned, maxLen);
  };

  const sanitizeHouseNumber = (value: string, maxLen: number) => {
    const cleaned = value.replace(/[^0-9A-Za-zÀ-ÿ\-/ ]/g, '');
    return trimMax(cleaned, maxLen);
  };

  const sanitizePlz = (value: string) => value.replace(/[^0-9]/g, '').slice(0, 5);

  const sanitizeOrt = (value: string, maxLen: number) => {
    const cleaned = value.replace(/[^A-Za-zÀ-ÿ.' -]/g, '');
    return trimMax(cleaned, maxLen);
  };

  const parsePreisProzent = (preisWert: any) => {
    if (typeof preisWert === 'number' && !Number.isNaN(preisWert)) {
      return preisWert;
    }

    if (typeof preisWert === 'string') {
      const normalisiert = preisWert.replace('%', '').replace(',', '.').trim();
      const parsed = Number.parseFloat(normalisiert);

      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    return 100;
  };

  const normalisiereAufladebetragEingabe = (value: string) => {
    const nurErlaubteZeichen = value.replace(/[^0-9,.]/g, '');
    const mitKomma = nurErlaubteZeichen.replace(/\./g, ',');
    const teile = mitKomma.split(',');
    const ganzzahlTeil = (teile[0] || '').replace(/^0+(?=\d)/, '');
    const nachkommaTeil = teile.slice(1).join('').slice(0, 2);

    if (nachkommaTeil) {
      return `${ganzzahlTeil || '0'},${nachkommaTeil}`;
    }

    if (mitKomma.startsWith(',')) {
      return '0,';
    }

    return ganzzahlTeil;
  };

  const aufladebetragFuerApi = (value: string) => {
    const normalisiert = normalisiereAufladebetragEingabe(value);
    if (!normalisiert) {
      return '';
    }

    const numerisch = Number.parseFloat(normalisiert.replace(',', '.'));
    if (Number.isNaN(numerisch)) {
      return '';
    }

    return numerisch.toFixed(2);
  };

  const formatiereAufladebetragAnzeige = (value: string) => {
    const apiWert = aufladebetragFuerApi(value);
    if (!apiWert) {
      return '';
    }

    return apiWert.replace('.', ',');
  };

  const [ vorname, setVorname ] = useState( "Lieschen" );
  const [ nachname, setNachname ] = useState( "Müller" );
  const [ schule, setSchule ] = useState( '' );
  const [ klasse, setKlasse ]:any = useState( '' );
  const [ showSpeichern, setShowSpeichern ]:any = useState( false );
  const [ showLS, setShowLS ]:any = useState( false );
  const [ zahlung, setZahlung ]:any = useState( "0" );
  const selectedSchuelerId = selectedSchuelerIdProp ?? appSelectedSchuelerId;
  const setSelectedSchuelerId = onSelectedSchuelerIdChange ?? appSelectedSchuelerIdChange;
  const [ datum, setDatum ] = useState( '' );
  const [ aufladebetrag, setAufladebetrag ] = useState<any>('');
  const [ dauerbestellung1, setDauerbestellung1 ] = useState('0');
  const [ dauerbestellung2, setDauerbestellung2 ] = useState('0');
  const [ dauerbestellung3, setDauerbestellung3 ] = useState('0');
  const [ dauerbestellung4, setDauerbestellung4 ] = useState('0');
  const [ dauerbestellung5, setDauerbestellung5 ] = useState('0');
  const [ dauerbestellungGueltigAb, setDauerbestellungGueltigAb ] = useState('2026-05-06');
  const [ dauerbestellungBestellungenLoeschen, setDauerbestellungBestellungenLoeschen ] = useState('0');
  const [ iban, setIban ] = useState('');
  const [ schuelerMandat, setSchuelerMandat ] = useState('');
  const [ kundenNachname, setKundenNachname ] = useState('');
  const [ kundenVorname, setKundenVorname ] = useState('');
  const [ kundenTelefon, setKundenTelefon ] = useState('');
  const [ kundenMobil, setKundenMobil ] = useState('');
  const [ kundenStrasse, setKundenStrasse ] = useState('');
  const [ kundenHausnummer, setKundenHausnummer ] = useState('');
  const [ kundenPlz, setKundenPlz ] = useState('');
  const [ kundenOrt, setKundenOrt ] = useState('');
  const [ kundenkontoInitial, setKundenkontoInitial ] = useState<any>({
    campusmenu_user_nachname: '',
    campusmenu_user_vorname: '',
    campusmenu_user_telefon: '',
    campusmenu_user_mobil: '',
    campusmenu_user_strasse: '',
    campusmenu_user_hausnummer: '',
    campusmenu_user_plz: '',
    campusmenu_user_ort: '',
  });
  const [ isKundenkontoSaving, setIsKundenkontoSaving ] = useState(false);
  const [ isSchuelerSaving, setIsSchuelerSaving ] = useState(false);
  const [ schoolOptions, setSchoolOptions ] = useState<SchoolOption[]>([]);
  const [ classOptionsBySchool, setClassOptionsBySchool ] = useState<Record<string, string[]>>({});
  const [ isLoadingSchools, setIsLoadingSchools ] = useState(false);
  const [ schuelerCardInitial, setSchuelerCardInitial ] = useState<any>({
    campusmenu_schueler_vorname: '',
    campusmenu_schueler_nachname: '',
    campusmenu_schueler_klasse: 'Klasse unbekannt',
    campusmenu_schueler_schule: '',
    campusmenu_schueler_geb_datum: '',
  });
  const [ zahlungCardInitial, setZahlungCardInitial ] = useState<any>({
    campusmenu_schueler_zahlung: '0',
    campusmenu_schueler_aufladebetrag: '',
    campusmenu_schueler_iban: '',
    campusmenu_schueler_mandat: '',
  });
  const [ dauerbestellungInitial, setDauerbestellungInitial ] = useState<any>({
    campusmenu_schueler_db_1: '0',
    campusmenu_schueler_db_2: '0',
    campusmenu_schueler_db_3: '0',
    campusmenu_schueler_db_4: '0',
    campusmenu_schueler_db_5: '0',
  });

  const selectedSchueler = schuelerListe.find(
    (schueler: any, index: number) => String(schueler.campusmenu_schueler_id || index) === String(selectedSchuelerId)
  );
  const selectedSchuelerData = selectedSchueler?.schuelerdaten || selectedSchueler || {};
  const schuelernummer = selectedSchueler?.campusmenu_schueler_id
    ? String(selectedSchueler.campusmenu_schueler_id).padStart(6, '0')
    : '000000';
  const mandatsreferenz = `S${schuelernummer}`;
  const verwendungszweck = `S${schuelernummer}`;
  const bankKontoInhaber = 'KBK Kunterbunte Kueche gGmbH';
  const bankGlaeubigerId = 'DE96HSG00001300757';
  const bankIbanAnzeige = 'DE88 2005 0550 1002 1888 68';
  const bankBic = 'HASPDEHHXXX';
  const epcIban = 'DE88200505501002188868';
  const epcBic = bankBic;
  const epcEmpfaenger = bankKontoInhaber;
  const epcPayload = [
    'BCD',
    '001',
    '1',
    'SCT',
    epcBic,
    epcEmpfaenger,
    epcIban,
    '',
    '',
    '',
    verwendungszweck,
    '',
  ].join('\n');
  const epcQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(epcPayload)}`;

  const aktuellesKundenkonto = {
    campusmenu_user_nachname: kundenNachname,
    campusmenu_user_vorname: kundenVorname,
    campusmenu_user_telefon: kundenTelefon,
    campusmenu_user_mobil: kundenMobil,
    campusmenu_user_strasse: kundenStrasse,
    campusmenu_user_hausnummer: kundenHausnummer,
    campusmenu_user_plz: kundenPlz,
    campusmenu_user_ort: kundenOrt,
  };

  const kundenkontoGeaendert =
    JSON.stringify(aktuellesKundenkonto) !== JSON.stringify(kundenkontoInitial);

  const aktuellesSchuelerCard = {
    campusmenu_schueler_vorname: vorname,
    campusmenu_schueler_nachname: nachname,
    campusmenu_schueler_klasse: klasse,
    campusmenu_schueler_schule: schule,
    campusmenu_schueler_geb_datum: datum,
  };

  const aktuellesZahlungCard = {
    campusmenu_schueler_zahlung: String(zahlung),
    campusmenu_schueler_aufladebetrag: String(aufladebetrag || ''),
    campusmenu_schueler_iban: iban,
    campusmenu_schueler_mandat: mandatsreferenz || schuelerMandat,
  };

  const schuelerCardGeaendert =
    JSON.stringify(aktuellesSchuelerCard) !== JSON.stringify(schuelerCardInitial);
  const zahlungCardGeaendert =
    JSON.stringify(aktuellesZahlungCard) !== JSON.stringify(zahlungCardInitial);

  const DAUERBESTELLUNG_KEINE_AENDERUNG = 'X';
  const aktuelleDauerbestellung = {
    campusmenu_schueler_db_1: dauerbestellung1,
    campusmenu_schueler_db_2: dauerbestellung2,
    campusmenu_schueler_db_3: dauerbestellung3,
    campusmenu_schueler_db_4: dauerbestellung4,
    campusmenu_schueler_db_5: dauerbestellung5,
  };
  const dauerbestellungFuerPayload = {
    campusmenu_schueler_db_1: dauerbestellung1 === DAUERBESTELLUNG_KEINE_AENDERUNG ? String(dauerbestellungInitial.campusmenu_schueler_db_1 || '0') : dauerbestellung1,
    campusmenu_schueler_db_2: dauerbestellung2 === DAUERBESTELLUNG_KEINE_AENDERUNG ? String(dauerbestellungInitial.campusmenu_schueler_db_2 || '0') : dauerbestellung2,
    campusmenu_schueler_db_3: dauerbestellung3 === DAUERBESTELLUNG_KEINE_AENDERUNG ? String(dauerbestellungInitial.campusmenu_schueler_db_3 || '0') : dauerbestellung3,
    campusmenu_schueler_db_4: dauerbestellung4 === DAUERBESTELLUNG_KEINE_AENDERUNG ? String(dauerbestellungInitial.campusmenu_schueler_db_4 || '0') : dauerbestellung4,
    campusmenu_schueler_db_5: dauerbestellung5 === DAUERBESTELLUNG_KEINE_AENDERUNG ? String(dauerbestellungInitial.campusmenu_schueler_db_5 || '0') : dauerbestellung5,
  };
  const dauerbestellungMinDatum = '2026-05-06';

  const dauerbestellungGeaendert =
    JSON.stringify(dauerbestellungFuerPayload) !== JSON.stringify(dauerbestellungInitial);
  const classOptions = useMemo(
    () => classOptionsBySchool[String(schule || '')] || [],
    [classOptionsBySchool, schule]
  );
  const classOptionsWithCurrent = useMemo(() => {
    const currentClass = String(klasse || '');
    if (!currentClass) {
      return classOptions;
    }

    if (classOptions.includes(currentClass)) {
      return classOptions;
    }

    return [currentClass, ...classOptions];
  }, [classOptions, klasse]);

  const parseJsonAntwort = async (response: Response) => {
    const raw = await response.text();
    if (!raw || !raw.trim()) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      throw new Error(`Ungültige Server-Antwort (${response.status}): ${raw.slice(0, 200)}`);
    }
  };

  const ladeVerzehrNeuFuerSchueler = async (schuelerId: any) => {
    if (!appToken || !schuelerId) {
      return;
    }

    const formData = new FormData();
    formData.append('appToken', appToken);
    formData.append('campusmenu_schueler_id', String(schuelerId));

    const response = await fetch(
      'https://login.saint-online.de/funktionen/api2/campusmenu/getGaesteVerzehr.php',
      { method: 'POST', body: formData }
    );
    const data = await parseJsonAntwort(response);

    if (!response.ok) {
      throw new Error(`Serverfehler bei getGaesteVerzehr (${response.status}).`);
    }

    if (!data) {
      throw new Error('Leere Server-Antwort bei getGaesteVerzehr.');
    }

    if (data.status != 200) {
      throw new Error(JSON.stringify(data.msg));
    }

    if (appWarenkorbChange) {
      appWarenkorbChange(JSON.stringify(data.data));
    }
  };

  const validiereIban = (roheIban: string): boolean => {
    const bereinigt = roheIban.replace(/\s+/g, '').toUpperCase();
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(bereinigt)) {
      return false;
    }
    const umgestellt = bereinigt.slice(4) + bereinigt.slice(0, 4);
    const numerisch = umgestellt.split('').map((c) => {
      const code = c.charCodeAt(0);
      return code >= 65 ? String(code - 55) : c;
    }).join('');
    let rest = 0;
    for (const ziffer of numerisch) {
      rest = (rest * 10 + Number(ziffer)) % 97;
    }
    return rest === 1;
  };

  const mergeSchuelerInUserData = (baseUserData: any, schuelerPayload: any) => {
    const nextUserData = baseUserData && typeof baseUserData === 'object' ? baseUserData : {};
    const schuelerId = String(schuelerPayload?.campusmenu_schueler_id || '');
    const bestehendeSchueler = Array.isArray(nextUserData.schueler) ? nextUserData.schueler : [];

    if (!schuelerId) {
      return nextUserData;
    }

    let schuelerGefunden = false;
    const naechsteSchueler = bestehendeSchueler.map((eintrag: any, index: number) => {
      const eintragId = String(
        eintrag?.campusmenu_schueler_id ||
        eintrag?.schuelerdaten?.campusmenu_schueler_id ||
        index
      );

      if (eintragId !== schuelerId) {
        return eintrag;
      }

      schuelerGefunden = true;

      return {
        ...eintrag,
        ...schuelerPayload,
        schuelerdaten: {
          ...(eintrag?.schuelerdaten || {}),
          ...schuelerPayload,
        },
      };
    });

    if (!schuelerGefunden) {
      naechsteSchueler.push({
        ...schuelerPayload,
        schuelerdaten: {
          ...schuelerPayload,
        },
      });
    }

    return {
      ...nextUserData,
      schueler: naechsteSchueler,
    };
  };

  const sendeDauerbestellungAlsVerzehr = async (schuelerId: string, gueltigAb: string) => {
    const start = new Date(`${gueltigAb}T00:00:00`);
    const end = new Date(2026, 6, 8);
    const current = new Date(start);
    const ausgeschlosseneDaten = new Set([
      '2026-05-11',
      '2026-05-12',
      '2026-05-13',
      '2026-05-14',
      '2026-05-15',
      '2026-01-01',
      '2026-04-03',
      '2026-04-06',
      '2026-05-01',
      '2026-05-14',
      '2026-05-25',
      '2026-10-03',
      '2026-10-31',
      '2026-12-25',
      '2026-12-26',
    ]);
    const menueProWochentag: Record<number, string> = {
      1: dauerbestellung1,
      2: dauerbestellung2,
      3: dauerbestellung3,
      4: dauerbestellung4,
      5: dauerbestellung5,
    };
    const schuelerPreisProzent = parsePreisProzent(selectedSchueler?.campusmenu_schueler_preis);
    const menuePreis = Math.round(5 * (schuelerPreisProzent / 100) * 100) / 100;
    const formatLocalDate = (date: Date) => {
      const jahr = date.getFullYear();
      const monat = String(date.getMonth() + 1).padStart(2, '0');
      const tag = String(date.getDate()).padStart(2, '0');
      return `${jahr}-${monat}-${tag}`;
    };

    const verzehrFormData = new FormData();
    verzehrFormData.append('appToken', appToken);
    verzehrFormData.append('campusmenu_schueler_id', schuelerId);
    const verzehrResponse = await fetch(
      'https://login.saint-online.de/funktionen/api2/campusmenu/getGaesteVerzehr.php',
      { method: 'POST', body: verzehrFormData }
    );
    const verzehrData = await parseJsonAntwort(verzehrResponse);
    if (!verzehrResponse.ok) {
      throw new Error(`Serverfehler bei getGaesteVerzehr (${verzehrResponse.status}).`);
    }
    if (!verzehrData || verzehrData.status != 200) {
      throw new Error('Aktueller Serverstand konnte nicht geladen werden.');
    }
    const serverEintraege: any[] = Array.isArray(verzehrData.data) ? verzehrData.data : [];

    if (appWarenkorbChange) {
      appWarenkorbChange(JSON.stringify(serverEintraege));
    }

    const bestehendeMenuesNachDatum = (() => {
      const eintraege = serverEintraege;
      const result: Record<string, { kategorie: number; menge: number; preis: number; verzehrId: number }> = {};

      eintraege.forEach((eintrag: any) => {
        const datum = String(
          eintrag?.saint_campusmenu_datum ||
          eintrag?.campusmenu_datum ||
          ''
        ).slice(0, 10);
        const kategorie = Number(
          eintrag?.saint_campusmenu_kategorie ||
          eintrag?.campusmenu_kategorie ||
          0
        );
        const menge = Number(
          eintrag?.saint_campusmenu_verzehr_menge ||
          eintrag?.campusmenu_verzehr_menge ||
          eintrag?.gaeste_verzehr_menge ||
          0
        );
        const preis = Number(
          eintrag?.saint_campusmenu_wert ||
          eintrag?.campusmenu_wert ||
          menuePreis
        );
        const verzehrId = Number(
          eintrag?.saint_campusmenu_verzehr_id ||
          eintrag?.campusmenu_verzehr_id ||
          eintrag?.gaeste_verzehr_id ||
          0
        );

        if (!datum || !(kategorie === 1 || kategorie === 2) || menge <= 0) {
          return;
        }

        const bestehend = result[datum];
        if (!bestehend || verzehrId > bestehend.verzehrId) {
          result[datum] = {
            kategorie,
            menge,
            preis: Number.isNaN(preis) ? menuePreis : preis,
            verzehrId,
          };
        }
      });

      return result;
    })();

    const bestellungen = [] as any[];
    const loeschenAktiv = String(dauerbestellungBestellungenLoeschen) === '1';

    while (current <= end) {
      const wochentag = current.getDay();
      const datumWert = formatLocalDate(current);

      if (wochentag >= 1 && wochentag <= 5 && !ausgeschlosseneDaten.has(datumWert)) {
        const menue = menueProWochentag[wochentag] || '0';

        if (menue === DAUERBESTELLUNG_KEINE_AENDERUNG) {
          current.setDate(current.getDate() + 1);
          continue;
        }

        const bestehend = bestehendeMenuesNachDatum[datumWert];
        if (!loeschenAktiv && bestehend) {
          current.setDate(current.getDate() + 1);
          continue;
        }

        if (loeschenAktiv && bestehend) {
          bestellungen.push({
            campusmenu_betriebe_id: 0,
            campusmenu_datum: datumWert,
            gaeste_verzehr_mahlzeit: 2,
            gaeste_verzehr_plan_id: 0,
            campusmenu_kategorie: Number(bestehend.kategorie),
            campusmenu_wert: Number(bestehend.preis || menuePreis),
            campusmenu_verzehr_menge: Math.max(1, Math.round(Number(bestehend.menge || 1))) * -1,
            gaeste_verzehr_schueler_id: Number(schuelerId),
            campusmenu_schueler_id: Number(schuelerId),
          });
        }

        if (menue !== '0') {
          bestellungen.push({
            campusmenu_betriebe_id: 0,
            campusmenu_datum: datumWert,
            gaeste_verzehr_mahlzeit: 2,
            gaeste_verzehr_plan_id: 0,
            campusmenu_kategorie: Number(menue),
            campusmenu_wert: menuePreis,
            campusmenu_verzehr_menge: 1,
            gaeste_verzehr_schueler_id: Number(schuelerId),
            campusmenu_schueler_id: Number(schuelerId),
          });
        }
      }

      current.setDate(current.getDate() + 1);
    }

    if (bestellungen.length === 0) {
      return;
    }

    const payload = {
      appToken,
      modus: 'batch',
      bestellungen,
    };

    const response = await fetch(
      'https://login.saint-online.de/funktionen/api2/campusmenu/setGaesteVerzehr.php',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    const data = await parseJsonAntwort(response);

    if (!response.ok) {
      throw new Error(`Serverfehler bei setGaesteVerzehr (${response.status}).`);
    }

    if (!data) {
      throw new Error('Leere Server-Antwort bei setGaesteVerzehr.');
    }

    if (data.status != 200) {
      throw new Error(JSON.stringify(data.msg));
    }
  };

  const speichernSchueler = async () => {
    if (!appToken) {
      alert('Bitte erneut anmelden. Kein gueltiger App-Token vorhanden.');
      return;
    }

    if (!selectedSchuelerData || Object.keys(selectedSchuelerData).length === 0) {
      alert('Bitte zuerst einen Schueler auswaehlen.');
      return;
    }

    if (dauerbestellungGeaendert && (!dauerbestellungGueltigAb || dauerbestellungGueltigAb < dauerbestellungMinDatum)) {
      alert('Bitte waehlen Sie ein gueltiges Startdatum ab dem 06.05. aus.');
      return;
    }

    if (zahlung === '1' || zahlung === '2') {
      if (!iban || !iban.trim()) {
        alert('Bitte geben Sie eine IBAN ein.');
        return;
      }
      if (!validiereIban(iban)) {
        alert('Die eingegebene IBAN ist ungültig. Bitte prüfen Sie die Eingabe.');
        return;
      }
    }

    setIsSchuelerSaving(true);

    const schuelerPayload = {
      ...selectedSchuelerData,
      campusmenu_schueler_id:
        selectedSchuelerData.campusmenu_schueler_id ||
        selectedSchueler?.campusmenu_schueler_id ||
        '',
      campusmenu_schueler_vorname: vorname,
      campusmenu_schueler_nachname: nachname,
      campusmenu_schueler_klasse: klasse,
      campusmenu_schueler_schule: schule,
      campusmenu_schueler_schule_id: schule,
      campusmenu_schueler_geb_datum: datum,
      campusmenu_schueler_zahlung: String(zahlung),
      campusmenu_schueler_aufladebetrag: aufladebetragFuerApi(String(aufladebetrag || '')),
      campusmenu_schueler_iban: iban,
      campusmenu_schueler_mandat: mandatsreferenz || schuelerMandat,
      campusmenu_schueler_verwendungszweck: verwendungszweck,
      campusmenu_schueler_db_1: dauerbestellungFuerPayload.campusmenu_schueler_db_1,
      campusmenu_schueler_db_2: dauerbestellungFuerPayload.campusmenu_schueler_db_2,
      campusmenu_schueler_db_3: dauerbestellungFuerPayload.campusmenu_schueler_db_3,
      campusmenu_schueler_db_4: dauerbestellungFuerPayload.campusmenu_schueler_db_4,
      campusmenu_schueler_db_5: dauerbestellungFuerPayload.campusmenu_schueler_db_5,
      campusmenu_schueler_db_gueltig_ab: dauerbestellungGueltigAb,
      campusmenu_schueler_db_bestellungen_loeschen: dauerbestellungBestellungenLoeschen,
    };

    const formData = new FormData();
    formData.append('appToken', appToken);

    Object.entries(schuelerPayload).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
        return;
      }

      formData.append(key, String(value));
    });

    try {
      const response = await fetch('https://login.saint-online.de/funktionen/api2/campusmenu/setSchueler.php', {
        method: 'POST',
        body: formData,
      });
      const data = await parseJsonAntwort(response);

      if (!response.ok) {
        throw new Error(`Serverfehler bei setSchueler (${response.status}).`);
      }

      if (!data) {
        throw new Error('Leere Server-Antwort bei setSchueler.');
      }

      if (data.status != 200) {
        throw new Error(JSON.stringify(data.msg));
      }

      if (dauerbestellungGeaendert) {
        const schuelerIdFuerVerzehr = String(
          schuelerPayload.campusmenu_schueler_id || selectedSchuelerId || ''
        );

        if (schuelerIdFuerVerzehr) {
          await sendeDauerbestellungAlsVerzehr(schuelerIdFuerVerzehr, dauerbestellungGueltigAb);

          if (appWarenkorbResetSignalChange) {
            appWarenkorbResetSignalChange(Math.random());
          }
        }
      }

      setSchuelerCardInitial(aktuellesSchuelerCard);
      setZahlungCardInitial(aktuellesZahlungCard);
      setDauerbestellung1(dauerbestellungFuerPayload.campusmenu_schueler_db_1);
      setDauerbestellung2(dauerbestellungFuerPayload.campusmenu_schueler_db_2);
      setDauerbestellung3(dauerbestellungFuerPayload.campusmenu_schueler_db_3);
      setDauerbestellung4(dauerbestellungFuerPayload.campusmenu_schueler_db_4);
      setDauerbestellung5(dauerbestellungFuerPayload.campusmenu_schueler_db_5);
      setDauerbestellungInitial(dauerbestellungFuerPayload);
      setShowSpeichern(false);

      const naechsteUserDataBasis = {
        ...(appUserData || {}),
        ...(data.data || {}),
      };

      appUserDataChange(mergeSchuelerInUserData(naechsteUserDataBasis, schuelerPayload));

      const schuelerIdFuerRefresh = String(
        schuelerPayload.campusmenu_schueler_id || selectedSchuelerId || ''
      );
      if (schuelerIdFuerRefresh) {
        await ladeVerzehrNeuFuerSchueler(schuelerIdFuerRefresh);
      }

      alert('Schuelerdaten gespeichert.');
    } catch (error: any) {
      alert(error?.message || 'Schuelerdaten konnten nicht gespeichert werden.');
    } finally {
      setIsSchuelerSaving(false);
    }
  };

  const speichernKundenkonto = () => {
    if (!appToken) {
      alert('Bitte erneut anmelden. Kein gueltiger App-Token vorhanden.');
      return;
    }

    setIsKundenkontoSaving(true);

    const formData = new FormData();
    formData.append('appToken', appToken);
    formData.append('campusmenu_user_nachname', kundenNachname);
    formData.append('campusmenu_user_vorname', kundenVorname);
    formData.append('campusmenu_user_telefon', kundenTelefon);
    formData.append('campusmenu_user_mobil', kundenMobil);
    formData.append('campusmenu_user_strasse', kundenStrasse);
    formData.append('campusmenu_user_hausnummer', kundenHausnummer);
    formData.append('campusmenu_user_plz', kundenPlz);
    formData.append('campusmenu_user_ort', kundenOrt);

    fetch('https://login.saint-online.de/funktionen/api2/campusmenu/setUser.php', {
      method: 'POST',
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status == 200) {
          setKundenkontoInitial(aktuellesKundenkonto);

          appUserDataChange({
            ...(appUserData || {}),
            ...(data.data || {}),
            ...aktuellesKundenkonto,
          });

          if (selectedSchuelerId) {
            ladeVerzehrNeuFuerSchueler(selectedSchuelerId).catch((error: any) => {
              console.error('getGaesteVerzehr konnte nach Speichern nicht geladen werden:', error?.message || error);
            });
          }

          alert('Kundenkonto gespeichert.');
        } else {
          alert(JSON.stringify(data.msg));
        }
      })
      .catch((error) => {
        alert(JSON.stringify(error.msg));
      })
      .finally(() => {
        setIsKundenkontoSaving(false);
      });
  };

  useEffect(() => {
    if (!setSelectedSchuelerId) {
      return;
    }

    if (schuelerListe.length === 0) {
      if (selectedSchuelerId !== null) {
        setSelectedSchuelerId(null);
      }
      return;
    }

    const istGueltigeAuswahl = schuelerListe.some(
      (schueler: any, index: number) =>
        String(schueler.campusmenu_schueler_id || index) === String(selectedSchuelerId)
    );

    if (!istGueltigeAuswahl) {
      const fallbackSchuelerId = String(schuelerListe[0].campusmenu_schueler_id || 0);
      setSelectedSchuelerId(fallbackSchuelerId);
    }
  }, [schuelerListe, selectedSchuelerId, setSelectedSchuelerId]);

  useEffect(() => {
    let isMounted = true;

    const loadSchoolsAndClasses = async () => {
      if (!appToken) {
        if (isMounted) {
          setSchoolOptions([]);
          setClassOptionsBySchool({});
          setIsLoadingSchools(false);
        }
        return;
      }

      setIsLoadingSchools(true);

      try {
        const formData = new FormData();
        formData.append('appToken', appToken);

        const response = await fetch(
          'https://login.saint-online.de/funktionen/api2/campusmenu/getKlassen.php',
          { method: 'POST', body: formData }
        );

        const raw = await response.text();
        const parsed = raw ? JSON.parse(raw) : null;
        const parsedData = parseSchoolsAndClasses(parsed?.data);

        if (!isMounted) {
          return;
        }

        setSchoolOptions(parsedData.schools);
        setClassOptionsBySchool(parsedData.classMap);
      } catch {
        if (isMounted) {
          setSchoolOptions([]);
          setClassOptionsBySchool({});
          alert('Schulen und Klassen konnten nicht geladen werden.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingSchools(false);
        }
      }
    };

    loadSchoolsAndClasses();

    return () => {
      isMounted = false;
    };
  }, [appToken]);

  useEffect(() => {
    if (!selectedSchueler) {
      return;
    }

    setVorname(selectedSchueler.campusmenu_schueler_vorname || '');
    setNachname(selectedSchueler.campusmenu_schueler_nachname || '');
    const schuelerKlasse =
      selectedSchueler?.schuelerdaten?.campusmenu_schueler_klasse ||
      selectedSchueler?.campusmenu_schueler_klasse ||
      '';
    setKlasse(schuelerKlasse);
    setSchule(
      selectedSchueler.campusmenu_schueler_schule ||
      selectedSchueler.campusmenu_schueler_schule_id ||
      ''
    );
    setDatum(getSchuelerDatum(selectedSchueler));
    setZahlung(String(selectedSchueler.campusmenu_schueler_zahlung ?? '0'));

    const initialSchueler = {
      campusmenu_schueler_vorname: selectedSchueler.campusmenu_schueler_vorname || '',
      campusmenu_schueler_nachname: selectedSchueler.campusmenu_schueler_nachname || '',
      campusmenu_schueler_klasse: schuelerKlasse,
      campusmenu_schueler_schule:
        selectedSchueler.campusmenu_schueler_schule ||
        selectedSchueler.campusmenu_schueler_schule_id ||
        '',
      campusmenu_schueler_geb_datum: getSchuelerDatum(selectedSchueler),
    };

    const initialZahlung = {
      campusmenu_schueler_zahlung: String(selectedSchueler.campusmenu_schueler_zahlung ?? '0'),
      campusmenu_schueler_aufladebetrag: formatiereAufladebetragAnzeige(
        String(
          selectedSchueler.campusmenu_schueler_aufladebetrag ||
          selectedSchueler.schuelerdaten?.campusmenu_schueler_aufladebetrag ||
          ''
        )
      ),
      campusmenu_schueler_iban:
        selectedSchueler.campusmenu_schueler_iban ||
        selectedSchueler.schuelerdaten?.campusmenu_schueler_iban ||
        '',
      campusmenu_schueler_mandat:
        selectedSchueler.campusmenu_schueler_mandat ||
        selectedSchueler.schuelerdaten?.campusmenu_schueler_mandat ||
        mandatsreferenz,
    };

    const initialDauerbestellung = {
      campusmenu_schueler_db_1: String(
        selectedSchueler.campusmenu_schueler_db_1 ||
        selectedSchueler.schuelerdaten?.campusmenu_schueler_db_1 ||
        '0'
      ),
      campusmenu_schueler_db_2: String(
        selectedSchueler.campusmenu_schueler_db_2 ||
        selectedSchueler.schuelerdaten?.campusmenu_schueler_db_2 ||
        '0'
      ),
      campusmenu_schueler_db_3: String(
        selectedSchueler.campusmenu_schueler_db_3 ||
        selectedSchueler.schuelerdaten?.campusmenu_schueler_db_3 ||
        '0'
      ),
      campusmenu_schueler_db_4: String(
        selectedSchueler.campusmenu_schueler_db_4 ||
        selectedSchueler.schuelerdaten?.campusmenu_schueler_db_4 ||
        '0'
      ),
      campusmenu_schueler_db_5: String(
        selectedSchueler.campusmenu_schueler_db_5 ||
        selectedSchueler.schuelerdaten?.campusmenu_schueler_db_5 ||
        '0'
      ),
    };

    setAufladebetrag(initialZahlung.campusmenu_schueler_aufladebetrag);
    setIban(initialZahlung.campusmenu_schueler_iban);
    setSchuelerMandat(initialZahlung.campusmenu_schueler_mandat);
    setDauerbestellung1(initialDauerbestellung.campusmenu_schueler_db_1);
    setDauerbestellung2(initialDauerbestellung.campusmenu_schueler_db_2);
    setDauerbestellung3(initialDauerbestellung.campusmenu_schueler_db_3);
    setDauerbestellung4(initialDauerbestellung.campusmenu_schueler_db_4);
    setDauerbestellung5(initialDauerbestellung.campusmenu_schueler_db_5);
    setDauerbestellungGueltigAb(dauerbestellungMinDatum);
    setDauerbestellungBestellungenLoeschen('0');

    setSchuelerCardInitial(initialSchueler);
    setZahlungCardInitial(initialZahlung);
    setDauerbestellungInitial(initialDauerbestellung);
  }, [selectedSchueler]);

  useEffect(() => {
    const initialData = {
      campusmenu_user_nachname: appUserData?.campusmenu_user_nachname || '',
      campusmenu_user_vorname: appUserData?.campusmenu_user_vorname || '',
      campusmenu_user_telefon: appUserData?.campusmenu_user_telefon || '',
      campusmenu_user_mobil: appUserData?.campusmenu_user_mobil || '',
      campusmenu_user_strasse: appUserData?.campusmenu_user_strasse || '',
      campusmenu_user_hausnummer: appUserData?.campusmenu_user_hausnummer || '',
      campusmenu_user_plz: appUserData?.campusmenu_user_plz || '',
      campusmenu_user_ort: appUserData?.campusmenu_user_ort || '',
    };

    setKundenNachname(initialData.campusmenu_user_nachname);
    setKundenVorname(initialData.campusmenu_user_vorname);
    setKundenTelefon(initialData.campusmenu_user_telefon);
    setKundenMobil(initialData.campusmenu_user_mobil);
    setKundenStrasse(initialData.campusmenu_user_strasse);
    setKundenHausnummer(initialData.campusmenu_user_hausnummer);
    setKundenPlz(initialData.campusmenu_user_plz);
    setKundenOrt(initialData.campusmenu_user_ort);
    setKundenkontoInitial(initialData);
  }, [appUserData]);

  

  useEffect(()=>{
    if(zahlung==="1" || zahlung==="2"){
      setShowLS(true)
    }
    else{
      setShowLS(false)
    }
  },[zahlung])

  const showKundenkonto = section === 'all' || section === 'kundenkonto';
  const showSchuelerkonto = section === 'all' || section === 'schuelerkonto';
  const modalTitle = section === 'kundenkonto' ? 'Kundenkonto' : section === 'schuelerkonto' ? 'Schülerkonto' : 'Einstellungen';

  const handleAddFurtherChild = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
    history.push('/student-data', { lockOrderTypeToChild: true });
  };

  return (
    <IonPage>
      <IonHeader>
        {embedded ? (
          <IonToolbar>
            <IonTitle>{modalTitle}</IonTitle>
            <IonButton slot="end" fill="clear" onClick={onClose}>Schließen</IonButton>
          </IonToolbar>
        ) : (
          <Toolbar />
        )}
      </IonHeader>
      <IonContent fullscreen>
        {showKundenkonto ? (
          <>
        <h2 style={{ padding: '0 16px', color:"var(--ion-color-primary)" }}>Kundenkonto</h2>
        <IonCard>
          <IonCardContent>
            <IonList>
              <IonItem>
                <IonLabel position="stacked">Kundennummer</IonLabel>
                {kundennummer}
              </IonItem>
              <IonItem>
                <IonLabel position='floating'>Nachname</IonLabel>
                <IonInput
                  type="text"
                  maxlength={60}
                  value={kundenNachname}
                  onIonInput={(e:any) => setKundenNachname(sanitizeName(e.detail.value || '', 60))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position='floating'>Vorname</IonLabel>
                <IonInput
                  type="text"
                  maxlength={60}
                  value={kundenVorname}
                  onIonInput={(e:any) => setKundenVorname(sanitizeName(e.detail.value || '', 60))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position='floating'>Telefon</IonLabel>
                <IonInput
                  type="tel"
                  inputmode="tel"
                  maxlength={25}
                  value={kundenTelefon}
                  onIonInput={(e:any) => setKundenTelefon(sanitizePhone(e.detail.value || '', 25))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position='floating'>Mobiltelefon</IonLabel>
                <IonInput
                  type="tel"
                  inputmode="tel"
                  maxlength={25}
                  value={kundenMobil}
                  onIonInput={(e:any) => setKundenMobil(sanitizePhone(e.detail.value || '', 25))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position='floating'>Straße</IonLabel>
                <IonInput
                  type="text"
                  maxlength={80}
                  value={kundenStrasse}
                  onIonInput={(e:any) => setKundenStrasse(sanitizeStreet(e.detail.value || '', 80))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position='floating'>Hausnummer</IonLabel>
                <IonInput
                  type="text"
                  maxlength={10}
                  value={kundenHausnummer}
                  onIonInput={(e:any) => setKundenHausnummer(sanitizeHouseNumber(e.detail.value || '', 10))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position='floating'>PLZ</IonLabel>
                <IonInput
                  type="text"
                  inputmode="numeric"
                  maxlength={5}
                  value={kundenPlz}
                  onIonInput={(e:any) => setKundenPlz(sanitizePlz(e.detail.value || ''))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position='floating'>Ort</IonLabel>
                <IonInput
                  type="text"
                  maxlength={80}
                  value={kundenOrt}
                  onIonInput={(e:any) => setKundenOrt(sanitizeOrt(e.detail.value || '', 80))}
                />
              </IonItem>
            </IonList>
            {kundenkontoGeaendert ? (
              <IonButton
                expand="block"
                onClick={speichernKundenkonto}
                disabled={isKundenkontoSaving}
              >
                {isKundenkontoSaving ? 'Speichert...' : 'Kundenkonto speichern'}
              </IonButton>
            ) : null}
          </IonCardContent>
        </IonCard>
          </>
        ) : null}

        {showSchuelerkonto ? (
          <>
        <h2 style={{ padding: '0 16px', color:"var(--ion-color-primary)" }}>Schülerkonto</h2>
        <IonCard >
          <IonCardHeader>
            <IonCardSubtitle>
              <div id="schuelerSelect" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {schuelerListe.map((schueler: any, index: number) => {
                  const optionValue = String(schueler.campusmenu_schueler_id || index);
                  const isSelected = String(optionValue) === String(selectedSchuelerId);

                  return (
                    <IonChip
                      key={optionValue}
                      onClick={() => setSelectedSchuelerId(optionValue)}
                      style={{
                        cursor: 'pointer',
                        background: isSelected ? '#ffffff' : '#d3d3d3',
                        color: isSelected ? 'var(--ion-color-primary)' : 'var(--ion-color-dark)',
                        border: isSelected ? '1px solid var(--ion-color-primary)' : '1px solid #b8b8b8',
                      }}
                    >
                      <IonLabel>{getSchuelerLabel(schueler)}</IonLabel>
                    </IonChip>
                  );
                })}
              </div>
              <IonButton expand="block" fill="outline" onClick={handleAddFurtherChild} style={{ marginTop: '12px' }}>
                Weiteres Kind hinzufügen
              </IonButton>
              {/* <IonButton
              onClick={()=>{
                setNachname('')
                setVorname('')
                setSchule('')
                setKlasse('')
                setDatum('')
                setZahlung('0')
                setAufladebetrag('')
                setShowSpeichern(true)
              }}
              >zusätzliches Kind anlegen</IonButton>    */}           
              </IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
              {selectedSchuelerId ? (<>
              <IonList>
                <IonItem>
                  <IonLabel position='stacked'>Schülernummer</IonLabel>
                  {`S${schuelernummer}`}
                </IonItem>
                <IonItem>
                  <IonLabel position='floating'>Vorname</IonLabel>
                  <IonInput type="text" value={vorname} onIonInput={(e:any) => setVorname(e.detail.value || '')}/>
                </IonItem>
                <IonItem>
                  <IonLabel position='floating'>Nachname</IonLabel>
                  <IonInput type="text" value={nachname} onIonInput={(e:any) => setNachname(e.detail.value || '')}/>
                </IonItem>
                <IonItem>
                  <IonLabel position='floating'>Schule</IonLabel>
                  <IonSelect
                    value={schule}
                    onIonChange={(e:any) => setSchule(e.detail.value || '')}
                    disabled={isLoadingSchools || schoolOptions.length === 0}
                  >
                    {schoolOptions.map((school) => (
                      <IonSelectOption key={school.value} value={school.value}>
                        {school.label}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
                <IonItem>
                  <IonLabel position='floating'>Klasse</IonLabel>
                  <IonSelect
                    value={klasse}
                    onIonChange={(e:any) => setKlasse(e.detail.value || '')}
                    disabled={classOptionsWithCurrent.length === 0}
                  >
                    {classOptionsWithCurrent.map((klassenWert) => (
                      <IonSelectOption key={klassenWert} value={klassenWert}>
                        {klassenWert}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>
                <IonItem>
                  <IonLabel position='floating'>Datum</IonLabel>
                  <IonInput type="date" value={datum} onIonInput={(e:any) => setDatum(e.detail.value || '')}/>
                </IonItem>
              </IonList>
              {schuelerCardGeaendert ? (
                <IonButton
                  expand="block"
                  onClick={speichernSchueler}
                  disabled={isSchuelerSaving}
                >
                  {isSchuelerSaving ? 'Speichert...' : 'Schuelerkonto speichern'}
                </IonButton>
              ) : null}
              {showSpeichern ? <IonButton onClick={()=>{setShowSpeichern(false)
                setNachname('Müller')
                setVorname('Lieschen')
                setKlasse('1')
                setSchule('1')
                setDatum('')
                setZahlung('0')
                setAufladebetrag('')

              }}>speichern</IonButton>:null}
              </>) : null}
          </IonCardContent>
        </IonCard>
        {selectedSchuelerId ? (<>
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Zahlung</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
              <IonItem>
                <IonLabel position='floating'>Zahlungsart</IonLabel>
                <IonSelect value={zahlung}
                onIonChange={(e)=>setZahlung(e.detail.value)}
                >
                  <IonSelectOption value="0">Überweisung</IonSelectOption>
                  <IonSelectOption value="1">Lastschrift manuell</IonSelectOption>
                  <IonSelectOption value="2">Lastschrift automatisch</IonSelectOption>
                </IonSelect>
              </IonItem>
              {zahlung === "0" ? (
                <IonItem lines="none">
                  <IonLabel>
                    Bitte überweisen Sie den offenen Betrag auf das folgende Konto: <br/>Kontoinhaber: {bankKontoInhaber}<br/>IBAN: {bankIbanAnzeige}<br/>BIC: {bankBic}<br/>Gläubiger-ID: {bankGlaeubigerId}<br/>Verwendungszweck: {verwendungszweck} <br/><br/>
Achten Sie bitte darauf, dass das Verbuchen der Gutschrift ein paar Tage dauern kann. Sobald die Gutschrift verbucht ist, kann ihr Kind das Essen erhalten. Bestellungen sind auch ohne ausreichende Deckung möglich, allerdings wird die Abholung verweigert, wenn das Konto nicht gedeckt ist.
                  </IonLabel>
                </IonItem>
              ) : null}
              {zahlung === "0" ? (
                <IonItem lines="none">
                  <IonLabel>
                    Um Tippfehler zu vermeiden, können Sie auch einfach den EPC-Code in Ihrer Banking-App scannen. Dadurch werden die Überweisungsdaten automatisch ausgefüllt. Bitte überprüfen Sie die Daten vor dem Absenden der Überweisung.
                    <IonImg src={epcQrUrl} alt="EPC QR-Code fuer Ueberweisung" style={{ width: '220px', maxWidth: '100%', marginTop: '8px' }} />
                  </IonLabel>
                </IonItem>
              ) : null}
              {zahlung === "1" ? (
                <IonItem lines="none">
                  <IonLabel>
                    Lastschrift manuell - Die Lastschriften werden beim Bestellvorgang durch &quot;Lastschrift jetzt einziehen&quot; eingezogen. Das Guthaben wird sofort auf dem Schüler-Konto -unter dem Vorbehalt der Wertstellung- gutgeschrieben.
<br /><br />
                    (A) Ermächtigung zum Einzug<br />
<span style={{ fontSize: '0.7em' }}>Ich ermächtige die Kunterbunten Küche, Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die von die Kunterbunten Küche auf mein Konto gezogenen Lastschriften einzulösen.</span>
<br /><br />
(B) Hinweis zum Widerruf und zur Erstattung<br />
<span style={{ fontSize: '0.7em' }}>Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit meinem Kreditinstitut vereinbarten Bedingungen.</span>
                  </IonLabel>
                </IonItem>
              ) : null}
              {zahlung === "2" ? (
                <IonItem lines="none">
                  <IonLabel>
                    Lastschrift automatisch - Ihr Guthaben wird automatisch um den eingestellten Aufladebetrag per Lastschrift erhöht. Ist das Guthaben verbraucht, wird eine erneute Lastschrift ausgelöst.
                  <br /><br />
                    (A) Ermächtigung zum Einzug<br />
<span style={{ fontSize: '0.7em' }}>Ich ermächtige die Kunterbunten Küche, Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die von die Kunterbunten Küche auf mein Konto gezogenen Lastschriften einzulösen.</span>
<br /><br />
(B) Hinweis zum Widerruf und zur Erstattung<br />
<span style={{ fontSize: '0.7em' }}>Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit meinem Kreditinstitut vereinbarten Bedingungen.</span>
                  </IonLabel>
                </IonItem>
              ) : null}
              {zahlung === "2" ? (
                <IonItem>
                  <IonLabel position='stacked'>Aufladebetrag</IonLabel>
                  <IonInput
                    type="text"
                    inputmode="decimal"
                    value={aufladebetrag}
                    onIonInput={(e:any) => {
                      const value = normalisiereAufladebetragEingabe(String(e.detail.value || ''));
                      setAufladebetrag(value);
                    }}
                    onIonBlur={() => {
                      setAufladebetrag((prev: string) => formatiereAufladebetragAnzeige(String(prev || '')));
                    }}
                  />
                  <IonLabel slot="end">€</IonLabel>
                </IonItem>
              ) : null}
{
  showLS ?
  <>
  <IonItem>
    <IonLabel position='floating'>IBAN</IonLabel>
    <IonInput
      type="text"
      value={iban}
      onIonInput={(e:any) => {
        const value = String(e.detail.value || '')
          .toUpperCase()
          .replace(/[^A-Z0-9 ]/g, '')
          .slice(0, 34);
        setIban(value);
      }}
    />
  </IonItem>

  <IonItem>
    <IonLabel position='floating'>Konto-Inhaber</IonLabel>
    <IonInput type="text" value={kontoInhaber} readonly />
  </IonItem>

  <IonItem>
    <IonLabel position='floating'>Mandatsreferenz</IonLabel>
    <IonInput type="text" value={mandatsreferenz || schuelerMandat} readonly />
  </IonItem>
  </>
  :
  null
}

              {zahlungCardGeaendert ? (
                <IonButton
                  expand="block"
                  onClick={speichernSchueler}
                  disabled={isSchuelerSaving}
                >
                  {isSchuelerSaving ? 'Speichert...' : 'Zahlungsdaten speichern'}
                </IonButton>
              ) : null}

            </IonList>
          </IonCardContent>
        </IonCard>
        <IonCard>
          <IonCardHeader>
          <IonCardTitle>
            Unverträglichkeiten
          </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            - keine hinterlegt -<br />
            Bitte im Servicecenter melden
          </IonCardContent>
        </IonCard>
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Dauerbestellung</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
             <IonItem>
                <IonLabel>Montag</IonLabel>
                <IonSelect value={dauerbestellung1} onIonChange={(e:any) => setDauerbestellung1(String(e.detail.value || '0'))}>
                  <IonSelectOption value={DAUERBESTELLUNG_KEINE_AENDERUNG}>Keine Änderung</IonSelectOption>
                  <IonSelectOption value="0">Kein Essen</IonSelectOption>
                  <IonSelectOption value="1">Menü 1</IonSelectOption>
                  <IonSelectOption value="2">Menü 2</IonSelectOption>
                </IonSelect>
             </IonItem>
             <IonItem>
                <IonLabel>Dienstag</IonLabel>
                <IonSelect value={dauerbestellung2} onIonChange={(e:any) => setDauerbestellung2(String(e.detail.value || '0'))}>
                  <IonSelectOption value={DAUERBESTELLUNG_KEINE_AENDERUNG}>Keine Änderung</IonSelectOption>
                  <IonSelectOption value="0">Kein Essen</IonSelectOption>
                  <IonSelectOption value="1">Menü 1</IonSelectOption>
                  <IonSelectOption value="2">Menü 2</IonSelectOption>
                </IonSelect>
             </IonItem>
             <IonItem>
                <IonLabel>Mittwoch</IonLabel>
                <IonSelect value={dauerbestellung3} onIonChange={(e:any) => setDauerbestellung3(String(e.detail.value || '0'))}>
                  <IonSelectOption value={DAUERBESTELLUNG_KEINE_AENDERUNG}>Keine Änderung</IonSelectOption>
                  <IonSelectOption value="0">Kein Essen</IonSelectOption>
                  <IonSelectOption value="1">Menü 1</IonSelectOption>
                  <IonSelectOption value="2">Menü 2</IonSelectOption>
                </IonSelect>
             </IonItem>
             <IonItem>
                <IonLabel>Donnerstag</IonLabel>
                <IonSelect value={dauerbestellung4} onIonChange={(e:any) => setDauerbestellung4(String(e.detail.value || '0'))}>
                  <IonSelectOption value={DAUERBESTELLUNG_KEINE_AENDERUNG}>Keine Änderung</IonSelectOption>
                  <IonSelectOption value="0">Kein Essen</IonSelectOption>
                  <IonSelectOption value="1">Menü 1</IonSelectOption>
                  <IonSelectOption value="2">Menü 2</IonSelectOption>
                </IonSelect>
             </IonItem>
             <IonItem>
                <IonLabel>Freitag</IonLabel>
                <IonSelect value={dauerbestellung5} onIonChange={(e:any) => setDauerbestellung5(String(e.detail.value || '0'))}>
                  <IonSelectOption value={DAUERBESTELLUNG_KEINE_AENDERUNG}>Keine Änderung</IonSelectOption>
                  <IonSelectOption value="0">Kein Essen</IonSelectOption>
                  <IonSelectOption value="1">Menü 1</IonSelectOption>
                  <IonSelectOption value="2">Menü 2</IonSelectOption>
                </IonSelect>
             </IonItem>
             {dauerbestellungGeaendert ? (
              <>
                <IonItem>
                  <IonLabel position='stacked'>Ab wann sollen die Dauerbestellungen gelten?</IonLabel>
                  <IonInput
                    type="date"
                    min={dauerbestellungMinDatum}
                    value={dauerbestellungGueltigAb}
                    onIonInput={(e:any) => {
                      const value = String(e.detail.value || '');
                      setDauerbestellungGueltigAb(value && value >= dauerbestellungMinDatum ? value : dauerbestellungMinDatum);
                    }}
                  />
                </IonItem>
                <IonItem>
                  <IonLabel>Sollen bereits getätigte Bestellungen gelöscht werden?</IonLabel>
                  <IonSelect
                    value={dauerbestellungBestellungenLoeschen}
                    onIonChange={(e:any) => setDauerbestellungBestellungenLoeschen(String(e.detail.value || '0'))}
                  >
                    <IonSelectOption value="0">Nein</IonSelectOption>
                    <IonSelectOption value="1">Ja</IonSelectOption>
                  </IonSelect>
                </IonItem>
              </>
             ) : null}
            </IonList>
            {dauerbestellungGeaendert ? (
              <IonButton
                expand="block"
                onClick={speichernSchueler}
                disabled={isSchuelerSaving}
              >
                {isSchuelerSaving ? 'Speichert...' : 'Dauerbestellung speichern'}
              </IonButton>
            ) : <><br /><br /><br /><br /><br /><br /><br /></>}
          </IonCardContent>
        </IonCard>
        </>) : null}
        </>
        ) : null}
        
      </IonContent>
    </IonPage>
  );
};

export default Settings;
